/**
 * 房间层状态机：纯函数、零 React、零 DOM、零副作用。
 *
 * 对局状态（`game`）只存在于这里，由 `useRoomSession` 用 `useReducer` 持有；
 * 连接/会话的 UI 状态才允许同步进 Zustand。
 *
 * 身份域严格分离（TC-1）：
 * - `self` / `opponent` 是**对局 playerId**（fog-chess 里就是棋色 `'r' | 'b'`），只它们能传给 `GameModule`；
 * - `opponentPeer` 是**传输层 peer id**，只用于传输与展示，绝不可传给 `GameModule`。
 * P0 的根因就是这两个域被混用：peer id 冒充 playerId 后，`fogChess.asColor` 把任何非 `'b'`
 * 的值静默折算成 `'r'`，于是房主把对方的走子当成自己的棋色校验并丢弃。
 *
 * T9 新增的观战模型（D 表见 `docs/tasks/T9.md`）：
 * - 房主是**星型中心**：新接入者先被房主判定成座位玩家还是观众，再回 `welcome`；
 * - 观众 `role === 'spectator'`、`self === null`，永远进不了 `isLegal` / `apply`；
 * - 房主独占「令牌表」（`identities`）与「走子序列」（`moveLog`），接入方靠 `sync` 重放补局；
 * - 定时器（宽限期）不在这里，见 `useRoomSession`。
 */

import type { Outcome, PlayerId } from '@games/core'
import type { ConnectionState } from '@games/net'

/** 房间生命周期的四个阶段。 */
export type RoomPhase = 'idle' | 'hosting' | 'joining' | 'playing'

/** 本方在房间里的角色。 */
export type RoomRole = 'host' | 'guest' | 'spectator'

/** 房主给玩家/观众的固定显示名。 */
export const HOST_NAME = '红方'
export const GUEST_NAME = '黑方'
/** 观众编号前缀：按接入顺序单调递增、**不回收**（嘉豪1 走了，下一个是嘉豪2）。 */
export const SPECTATOR_NAME_PREFIX = '嘉豪'

/** 房间层驱动一个具体小游戏所需的最小接口（`GameModule` 的结构子集）。 */
export interface ReducibleGame<S, M> {
  createInitialState(): S
  isLegal(state: S, move: M, playerId: PlayerId): boolean
  apply(state: S, move: M, playerId: PlayerId): S
  outcome(state: S): Outcome | null
}

/** 房主维护的一条接入者档案：令牌 → 座位/观众名/当前连接。 */
export interface RoomIdentity {
  readonly token: string
  readonly name: string
  /** `'guest'` = 座位玩家，`'spectator'` = 观众 */
  readonly role: 'guest' | 'spectator'
  readonly seat: PlayerId | null
  /** 当前连接；掉线后置 `null`，身份依旧保留（等它带同一令牌回来） */
  readonly peer: string | null
  readonly away: boolean
}

/** 走子序列里的一步：谁走的 + 走法。 */
export interface MoveLogEntry<M> {
  readonly by: PlayerId
  readonly move: M
}

/**
 * 房间层要发出去、但还没发出去的控制消息（发件箱）。
 *
 * 为什么用发件箱：接入判定（谁坐座位、叫什么名字）与补局内容（走子序列）
 * 全都是纯逻辑，放在 reducer 里就能单测；`useRoomSession` 只负责排空它。
 */
export type RoomOutgoing<M> =
  | {
    readonly type: 'welcome'
    readonly peer: string
    readonly role: 'player' | 'spectator'
    readonly seat: PlayerId | null
    readonly name: string
  }
  | { readonly type: 'sync'; readonly peer: string; readonly moves: readonly MoveLogEntry<M>[] }

export interface RoomState<S, M> {
  readonly phase: RoomPhase
  /** 本方角色；`spectator` 时 `self === null` */
  readonly role: RoomRole
  /** 本方显示名（房主分配） */
  readonly name: string | null
  readonly roomCode: string | null
  readonly shareLink: string | null
  /** 本方在游戏中的 playerId（fog-chess 里就是棋色 `'r' | 'b'`）——只它可传给 `GameModule` */
  readonly self: PlayerId | null
  /** 对方在游戏中的 playerId */
  readonly opponent: PlayerId | null
  /** 对方传输层 peer id */
  readonly opponentPeer: string | null
  readonly status: ConnectionState
  /** 直接可用于 UI 的中文提示 */
  readonly statusText: string
  readonly error: string | null
  readonly game: S | null
  /**
   * 语义：为 `true` 表示「本方已请求再来一局、正在等对方确认」。
   * 由 `restart.source` 决定：`'self'` 置 `true`，`'peer'` 置 `false`（对方的请求在本方立即生效，
   * 本方没有待确认的请求），`reset` 时清除。
   */
  readonly restartPending: boolean
  /** 从开局起的完整走子序列（含执子方）；`restart` 时清空。接入方靠它重放补局 */
  readonly moveLog: readonly MoveLogEntry<M>[]
  /** 身份表（**只有房主维护**）：接入方回来的座位/名字都靠它 */
  readonly identities: readonly RoomIdentity[]
  /** 下一个观众编号（从 1 开始，单调递增不回收） */
  readonly nextSpectatorNumber: number
  /**
   * 当前在线的观众人数（**所有人都能看到**，D12）。
   * 房主从身份表推导；其他人生效的是房主广播过来的值 —— 身份只有房主掌握。
   * 掉线宽限期内的观众不计入。
   */
  readonly audienceCount: number
  /** 对手是否处于「掉线等重连」的宽限期内 */
  readonly peerAway: boolean
  readonly outbox: readonly RoomOutgoing<M>[]
}

export type RoomAction<M> =
  | { type: 'host-started'; roomCode: string; shareLink: string }
  | { type: 'join-started'; roomCode: string }
  | { type: 'transport-state'; status: ConnectionState; error?: string }
  /** 房主收到接入方的自我介绍：令牌命中就归还座位/观众编号，否则发新身份 */
  | { type: 'hello'; token: string; peer: string }
  /** 接入方收到房主的身份指派（`role` 用的是协议口径：player / spectator） */
  | { type: 'welcome'; role: 'player' | 'spectator'; seat: PlayerId | null; name: string }
  | { type: 'sync-request'; peer: string }
  | { type: 'sync'; moves: readonly MoveLogEntry<M>[] }
  /** 房主广播的观众人数（非房主生效；房主自己从身份表推导，收到也不理） */
  | { type: 'audience'; count: number }
  | { type: 'outbox-flushed'; count: number }
  | { type: 'local-move'; move: M }
  /** `by` 只在房主中继给观众的走子上出现（点对点直发时不填，用 `opponent` 推导） */
  | { type: 'remote-move'; move: M; by?: PlayerId }
  | { type: 'restart'; source: 'self' | 'peer' }
  | { type: 'peer-away'; peer: string }
  | { type: 'peer-gone'; peer: string; reason: string }
  | { type: 'abort'; reason: string }
  | { type: 'reset' }

const STATUS_TEXT: Readonly<Record<ConnectionState, string>> = {
  idle: '未连接',
  signaling: '等待对方加入…',
  connecting: '正在建立通道…',
  connected: '已连接',
  failed: '连接失败',
  closed: '连接已断开',
}

/** 固定座位映射：房主执红（先手），加入方执黑。 */
const HOST_SEAT = 'r'
const GUEST_SEAT = 'b'

/** playerId 域只认合法座位；其余值（典型是传输层 peer id）一律不许进入。 */
function isSeat(value: PlayerId | null | undefined): value is typeof HOST_SEAT | typeof GUEST_SEAT {
  return value === HOST_SEAT || value === GUEST_SEAT
}

function oppositeSeat(self: PlayerId): PlayerId {
  return self === GUEST_SEAT ? HOST_SEAT : GUEST_SEAT
}

/**
 * 把外来 playerId 收敛到座位域：不是合法座位就按固定座位映射推导。
 * 这样即使调用方误把 peer id 传进 `opponent`，也不可能再被当成棋色参与校验。
 */
function toSeat(value: PlayerId | null | undefined, fallback: PlayerId): PlayerId {
  return isSeat(value) ? value : fallback
}

export const initialRoomState: RoomState<never, never> = {
  phase: 'idle',
  role: 'guest',
  name: null,
  roomCode: null,
  shareLink: null,
  self: null,
  opponent: null,
  opponentPeer: null,
  status: 'idle',
  statusText: STATUS_TEXT.idle,
  error: null,
  game: null,
  restartPending: false,
  moveLog: [],
  identities: [],
  nextSpectatorNumber: 1,
  audienceCount: 0,
  peerAway: false,
  outbox: [],
}

/** 对手/房主掉线时的宽限提示文案。 */
function awayText(role: RoomRole): string {
  // 加入方与观众连的都是房主，掉线的一方就是房主
  return role === 'host' ? '对方掉线，等待重连…' : '房主掉线，等待重连…'
}

/** 在线观众人数：宽限期内的不算（房主本人是玩家，本来就不在身份表里）。 */
function onlineAudience(identities: readonly RoomIdentity[]): number {
  return identities.filter((identity) => identity.role === 'spectator' && !identity.away).length
}

/**
 * 用具体游戏构造房间 reducer。
 * 所有走子动作在 `game === null` 或对应 playerId 缺失时原样返回，绝不抛异常。
 */
export function createRoomReducer<S, M>(
  game: ReducibleGame<S, M>,
): (state: RoomState<S, M>, action: RoomAction<M>) => RoomState<S, M> {
  return function roomReducer(state: RoomState<S, M>, action: RoomAction<M>): RoomState<S, M> {
    switch (action.type) {
      case 'host-started':
        return {
          ...state,
          phase: 'hosting',
          role: 'host',
          name: HOST_NAME,
          // 房主固定执红先手：座位在建房那一刻就定了，不必等 hello
          self: HOST_SEAT,
          roomCode: action.roomCode,
          shareLink: action.shareLink,
        }

      case 'join-started':
        return { ...state, phase: 'joining', role: 'guest', roomCode: action.roomCode }

      case 'transport-state':
        return {
          ...state,
          status: action.status,
          error: action.error ?? null,
          statusText: action.error ?? STATUS_TEXT[action.status],
        }

      case 'hello': {
        // 只有房主做接入判定（先到先得由房主这个单一权威说了算）
        if (state.role !== 'host') return state

        const known = state.identities.find((identity) => identity.token === action.token)
        if (known) {
          // 令牌命中：归还原身份（座位 / 原「嘉豪N」），即使座位此刻空着也一样
          const isPlayer = known.role === 'guest'
          const seat = known.seat ?? GUEST_SEAT
          const identities = state.identities.map((identity) =>
            identity.token === action.token
              ? { ...identity, peer: action.peer, away: false }
              : identity,
          )
          return {
            ...state,
            identities,
            // 回来的可能正是某位观众：在线人数跟着变
            audienceCount: onlineAudience(identities),
            outbox: [
              ...state.outbox,
              {
                type: 'welcome',
                peer: action.peer,
                role: isPlayer ? 'player' : 'spectator',
                seat: isPlayer ? seat : null,
                name: known.name,
              },
            ],
            ...(isPlayer
              ? {
                phase: 'playing' as const,
                opponent: seat,
                opponentPeer: action.peer,
                peerAway: false,
                error: null,
                statusText: STATUS_TEXT.connected,
              }
              : {}),
          }
        }

        const seatTaken = state.identities.some((identity) => identity.role === 'guest')
        const isPlayer = !seatTaken
        const name = isPlayer
          ? GUEST_NAME
          : `${SPECTATOR_NAME_PREFIX}${state.nextSpectatorNumber}`
        const identity: RoomIdentity = {
          token: action.token,
          name,
          role: isPlayer ? 'guest' : 'spectator',
          seat: isPlayer ? GUEST_SEAT : null,
          peer: action.peer,
          away: false,
        }
        return {
          ...state,
          identities: [...state.identities, identity],
          audienceCount: onlineAudience([...state.identities, identity]),
          // 编号不回收：观众走了也不把号还回去
          nextSpectatorNumber: isPlayer
            ? state.nextSpectatorNumber
            : state.nextSpectatorNumber + 1,
          outbox: [
            ...state.outbox,
            {
              type: 'welcome',
              peer: action.peer,
              role: isPlayer ? 'player' : 'spectator',
              seat: identity.seat,
              name,
            },
          ],
          ...(isPlayer
            ? {
              phase: 'playing' as const,
              opponent: GUEST_SEAT,
              opponentPeer: action.peer,
              // 新对手开新局；接入方自己会发 sync-request 补局
              game: game.createInitialState(),
              moveLog: [],
              error: null,
              statusText: STATUS_TEXT.connected,
            }
            : {}),
        }
      }

      case 'welcome': {
        if (state.role === 'host') return state
        const isPlayer = action.role === 'player' && action.seat !== null

        if (!isPlayer) {
          // 观众：没有座位、不参与 isLegal/apply，但要能看到棋局与聊天
          return {
            ...state,
            phase: 'playing',
            role: 'spectator',
            name: action.name,
            self: null,
            opponent: null,
            game: state.game ?? game.createInitialState(),
            moveLog: [],
            error: null,
            statusText: STATUS_TEXT.connected,
          }
        }
        const self = toSeat(action.seat, GUEST_SEAT)
        return {
          ...state,
          phase: 'playing',
          role: 'guest',
          name: action.name,
          self,
          opponent: oppositeSeat(self),
          game: game.createInitialState(),
          moveLog: [],
          error: null,
          statusText: STATUS_TEXT.connected,
        }
      }

      case 'sync-request':
        if (state.role !== 'host') return state
        return {
          ...state,
          outbox: [...state.outbox, { type: 'sync', peer: action.peer, moves: state.moveLog }],
        }

      case 'sync': {
        // 房主自己就是权威，不需要听别人补局
        if (state.role === 'host') return state
        let replay: S = game.createInitialState()
        for (const entry of action.moves) {
          if (!game.isLegal(replay, entry.move, entry.by)) {
            // 宁可报错，也不把一条非法的对局渲染成正常局面
            const text = '同步失败：对方给出的走子序列不合法。'
            return { ...state, error: text, statusText: text }
          }
          replay = game.apply(replay, entry.move, entry.by)
        }
        return {
          ...state,
          game: replay,
          moveLog: action.moves,
          error: null,
          statusText: STATUS_TEXT[state.status],
        }
      }

      case 'audience':
        // 房主自己是权威（从身份表推导），不需要听别人报数
        if (state.role === 'host') return state
        return { ...state, audienceCount: action.count }

      case 'outbox-flushed':
        return { ...state, outbox: state.outbox.slice(action.count) }

      case 'local-move': {
        const current = state.game
        if (current === null || state.self === null) return state
        // 必须用己方座位（棋色），不能用 peer id
        if (!game.isLegal(current, action.move, state.self)) return state
        return {
          ...state,
          game: game.apply(current, action.move, state.self),
          moveLog: [...state.moveLog, { by: state.self, move: action.move }],
        }
      }

      case 'remote-move': {
        const current = state.game
        // 观众只能靠房主中继里附带的 by 才知道执子方
        const actor = action.by ?? state.opponent
        if (current === null || actor === null) return state
        // 用执子方座位（棋色）校验；非法或非其回合的走子静默丢弃，不能因此中断对局
        if (!game.isLegal(current, action.move, actor)) return state
        return {
          ...state,
          game: game.apply(current, action.move, actor),
          moveLog: [...state.moveLog, { by: actor, move: action.move }],
        }
      }

      case 'restart':
        return {
          ...state,
          game: state.game === null ? null : game.createInitialState(),
          moveLog: [],
          restartPending: action.source === 'self',
          statusText: action.source === 'self' ? '已请求再来一局，等待对方…' : '对方请求再来一局',
        }

      case 'peer-away': {
        if (state.role !== 'host') {
          // 加入方与观众都只有一条连接（连房主），掉的就是房主
          return { ...state, peerAway: true, statusText: awayText(state.role) }
        }
        const identity = state.identities.find((item) => item.peer === action.peer)
        if (!identity) return state
        const identities = state.identities.map((item) =>
          item.peer === action.peer ? { ...item, away: true } : item,
        )
        const audienceCount = onlineAudience(identities)
        // 观众来去自由：只更新档案，绝不影响对局
        if (identity.role === 'spectator') return { ...state, identities, audienceCount }
        return {
          ...state,
          identities,
          audienceCount,
          peerAway: true,
          statusText: awayText(state.role),
        }
      }

      case 'peer-gone': {
        if (state.role === 'host') {
          const identity = state.identities.find((item) => item.peer === action.peer)
          if (identity && identity.role === 'spectator') {
            const identities = state.identities.filter((item) => item.peer !== action.peer)
            return {
              ...state,
              identities,
              audienceCount: onlineAudience(identities),
            }
          }
        }
        // 宽限期用尽：保留 game 用于展示终局画面，只把连接态与错误信息落下
        return {
          ...state,
          status: 'closed',
          error: action.reason,
          statusText: action.reason,
          peerAway: false,
        }
      }

      case 'abort':
        // 保留 game 用于展示终局画面，只把连接态与错误信息落下
        return { ...state, status: 'closed', error: action.reason, statusText: action.reason }

      case 'reset':
        return initialRoomState
    }
  }
}
